import { ActionModal, FormLayout } from '@kcftech/design-system';
import {
    IDataTypeResponse,
    INodeOutputSensorDataConfigSlot,
    IOutputSensorTypeResponse,
    IUnitResponse,
    SensorStandard,
} from '@kcftech/prometheus-client';
import { RadioButton } from 'components/forms/RadioButton';
import { RadioButtonItem } from 'components/forms/RadioButtonGroup';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { sdClient } from 'contexts/SDKClientContext';
import { DataTypeDropdown } from 'pages/Settings/ThirdPartySensorModels/DataTypeDropdown';
import { OutputDataStreamTypeDropdown } from 'pages/Settings/ThirdPartySensorModels/OutputDataStreamTypeDropdown';
import {
    displayNameRules,
    NotesMaxLength,
    notesRules,
} from 'pages/Settings/ThirdPartySensorModels/shared';
import {
    FormInnerContent,
    Heading,
    Row,
    StyledTextArea,
    StyledTextInput,
} from 'pages/Settings/ThirdPartySensorModels/styled';
import { UnitsDropdown } from 'pages/Settings/ThirdPartySensorModels/UnitsDropdown';
import {
    Controller,
    UseFormReturn,
    useForm,
    useWatch,
    useFormState,
} from 'react-hook-form';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { isValidNumber } from 'utils/number';
import { Filter } from 'utils/query_v3';

const RadioButtonsContainer = styled.div`
    display: flex;
    gap: 1rem;
    align-items: flex-end;
    padding-bottom: 1.2rem;
`;

const StyledRadioButton = styled(RadioButton)`
    background-color: ${tokens.background.default} !important;
`;

const validateNumber = (num: number | string | undefined, name: string) => {
    if (!num?.toString().trim()) {
        return `${name} is required.`;
    }

    if (!isValidNumber(num)) {
        return `${name} must be a valid number.`;
    }

    return undefined;
};

const unitSuffix = (str: string, unit: IUnitResponse | null) =>
    !unit ? str : `${str} (${unit.Abbreviation})`;

async function validateConfigName(value: string, sensorModelId?: string) {
    // if we don't have a model id, we're creating the config under a new model
    if (!sensorModelId) {
        return undefined;
    }

    if (!value.trim()) {
        return 'Please enter a configuration name.';
    }

    const exists = await sdClient
        .getOutputSensorDataConfigs({
            filter: Filter.and(
                Filter.caseInsensitiveEq('Name', value),
                Filter.eq('PhysicalSensorModelId', sensorModelId),
            ),
        })
        .then((results) => results.Total > 0);

    if (exists) {
        return `Configuration with name "${value}" already exists. Please enter a unique name.`;
    }

    return undefined;
}

export type Sensitivity = Readonly<{
    factor?: number;
    measurementLow?: number;
    measurementHigh?: number;
    outputLow?: number;
    outputHigh?: number;
    outputUnits?: TransducerSensitivityInputOutputUnitCode;
}>;

export type CreateConfigurationForm = Readonly<{
    displayName: string;
    dataType: IDataTypeResponse | null;
    outputDataStreamType: IOutputSensorTypeResponse | null;
    units: IUnitResponse | null;
    sensitivity: Sensitivity;
    notes: string;
}>;

/**
 * The hardware actually reports in V, but we'll specify the sensitivity in mV
 * and the backend will handle it.
 */
const IepeTransducerOutputUnitForUi = 'mV'; // a unit code

type TransducerSensitivityInputOutputUnitCode =
    | 'mA'
    | 'V'
    | typeof IepeTransducerOutputUnitForUi;
const OutputUnitOptions: RadioButtonItem<TransducerSensitivityInputOutputUnitCode>[] = [
    {
        value: 'mA', // a unit Code
        label: 'mA',
    },
    {
        value: 'V', // a unit Code
        label: 'V',
    },
];

export function useOutputConfigurationForm({
    defaultValues,
    sensorStandard,
}: {
    defaultValues?: CreateConfigurationForm;
    sensorStandard: SensorStandard;
}) {
    return useForm<CreateConfigurationForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: defaultValues ?? {
            sensitivity: {
                outputUnits:
                    sensorStandard === SensorStandard.Iepe
                        ? IepeTransducerOutputUnitForUi
                        : undefined,
            },
        },
    });
}

export type CreateConfigurationFormFieldsProps = Readonly<{
    sensorModelId?: string;
    sensorStandard: SensorStandard;
    slot?: INodeOutputSensorDataConfigSlot;
    form: UseFormReturn<CreateConfigurationForm, unknown>;
}>;

export type CreateConfigurationModalProps = Readonly<{
    sensorModelId?: string;
    defaultValues?: CreateConfigurationForm;
    sensorStandard: SensorStandard;
    slot?: INodeOutputSensorDataConfigSlot;
    onSave: (formData: CreateConfigurationForm) => void;
    isSaving?: boolean;
    onClose: () => void;
}>;

export function CreateConfigurationFormFields({
    sensorModelId,
    sensorStandard,
    slot,
    form,
}: CreateConfigurationFormFieldsProps) {
    const isAnalog = sensorStandard === SensorStandard.Analog;
    const {
        control,
        register,
        setValue,
    } = form;

    const {
        errors,
    } = useFormState({
        control,
    });

    const units = useWatch({ control, name: 'units' });
    const dataType = useWatch({ control, name: 'dataType' });

    return (
        <>
            <ValidatableLabel label="Display Name" required error={errors.displayName}>
                <StyledTextInput
                    placeholder="Enter a Display Name"
                    $lightBackground
                    {...register('displayName', {
                        ...displayNameRules,
                        validate: (value) => validateConfigName(value, sensorModelId),
                    })}
                />
            </ValidatableLabel>
            <ValidatableLabel label="Data Type" required error={errors.dataType}>
                <Controller
                    name="dataType"
                    control={control}
                    rules={{ required: 'Data Type is required.' }}
                    render={({ field: { onChange, value } }) => (
                        <DataTypeDropdown
                            slot={slot}
                            value={value ?? undefined}
                            onChange={(e) => {
                                onChange(e ?? null);
                                setValue('units', null);
                                setValue('outputDataStreamType', null);
                            }}
                        />
                    )}
                />
            </ValidatableLabel>
            <ValidatableLabel
                label="Output Data Stream Type"
                required
                error={errors.outputDataStreamType}
            >
                <Controller
                    name="outputDataStreamType"
                    control={control}
                    rules={{ required: 'Output Data Stream Type is required.' }}
                    render={({ field: { onChange, value } }) => (
                        <OutputDataStreamTypeDropdown
                            dataTypeId={dataType?.Id}
                            value={value ?? undefined}
                            onChange={(newValue) => onChange(newValue ?? null)}
                        />
                    )}
                />
            </ValidatableLabel>
            <ValidatableLabel label="Units" required error={errors.units}>
                <Controller
                    name="units"
                    control={control}
                    rules={{ required: 'Units are required.' }}
                    render={({ field: { onChange, value } }) => (
                        <UnitsDropdown
                            value={value ?? undefined}
                            onChange={(newValue) => onChange(newValue ?? null)}
                            disabled={!dataType?.Id}
                            physicalQuantityCode={dataType?.PhysicalQuantity.Code}
                        />
                    )}
                />
            </ValidatableLabel>
            {isAnalog ? (
                <div className="flex-column gap-8">
                    <Heading>Analog Settings</Heading>
                    <FormInnerContent>
                        <Heading>Physical Measurement Range</Heading>
                        <Row>
                            <ValidatableLabel
                                label={unitSuffix('Low', units)}
                                required
                                error={errors.sensitivity?.measurementLow}
                            >
                                <StyledTextInput
                                    placeholder="Low value"
                                    {...register('sensitivity.measurementLow', {
                                        required: 'Low value is required.',
                                        validate: (val) =>
                                            validateNumber(val, 'Low value'),
                                    })}
                                />
                            </ValidatableLabel>
                            <ValidatableLabel
                                label={unitSuffix('High', units)}
                                required
                                error={errors.sensitivity?.measurementHigh}
                            >
                                <StyledTextInput
                                    placeholder="High value"
                                    {...register('sensitivity.measurementHigh', {
                                        required: 'High value is required.',
                                        validate: (val) =>
                                            validateNumber(val, 'High value'),
                                    })}
                                />
                            </ValidatableLabel>
                        </Row>
                    </FormInnerContent>
                    <FormInnerContent>
                        <Heading>Transducer Output Range</Heading>
                        <Row $endColumn>
                            <ValidatableLabel
                                label="Low"
                                required
                                error={errors.sensitivity?.outputLow}
                            >
                                <StyledTextInput
                                    placeholder="Low value"
                                    {...register('sensitivity.outputLow', {
                                        required: 'Low value is required.',
                                        validate: (val) =>
                                            validateNumber(val, 'Low value'),
                                    })}
                                />
                            </ValidatableLabel>
                            <ValidatableLabel
                                label="High"
                                required
                                error={errors.sensitivity?.outputHigh}
                            >
                                <StyledTextInput
                                    placeholder="High value"
                                    {...register('sensitivity.outputHigh', {
                                        required: 'High value is required.',
                                        validate: (val) =>
                                            validateNumber(val, 'High value'),
                                    })}
                                />
                            </ValidatableLabel>
                            <Controller
                                name="sensitivity.outputUnits"
                                control={control}
                                defaultValue="mA"
                                render={({ field: { onChange, value } }) => (
                                    <RadioButtonsContainer>
                                        {OutputUnitOptions.map((option) => (
                                            <StyledRadioButton
                                                key={option.value}
                                                name="sensitivity.outputUnits"
                                                label={option.label}
                                                labelClassName="f-14"
                                                checked={option.value === value}
                                                onChange={() => onChange(option.value)}
                                            />
                                        ))}
                                    </RadioButtonsContainer>
                                )}
                            />
                        </Row>
                    </FormInnerContent>
                </div>
            ) : (
                <div className="flex-column">
                    <Heading className="pb-10">IEPE Settings</Heading>
                    <FormInnerContent>
                        <ValidatableLabel
                            label={`Sensitivity${
                                units
                                    ? ` (${IepeTransducerOutputUnitForUi}/${units.Abbreviation})`
                                    : ''
                            }`}
                            required
                            error={errors.sensitivity?.factor}
                        >
                            <StyledTextInput
                                placeholder="Sensitivity"
                                {...register('sensitivity.factor', {
                                    required: 'Factor is required.',
                                    validate: (val) => validateNumber(val, 'Factor'),
                                })}
                            />
                        </ValidatableLabel>
                    </FormInnerContent>
                </div>
            )}
            <ValidatableLabel label="Notes" error={errors.notes}>
                <StyledTextArea
                    className="mt-5"
                    placeholder="Please provide more detail for context"
                    maxLength={NotesMaxLength}
                    showLengthIndicator
                    $lightBackground
                    {...register('notes', notesRules)}
                />
            </ValidatableLabel>
        </>
    );
}

export function CreateConfigurationModal({
    sensorModelId,
    defaultValues,
    sensorStandard,
    slot,
    onSave,
    isSaving,
    onClose,
}: CreateConfigurationModalProps) {
    const isAnalog = sensorStandard === SensorStandard.Analog;
    const form = useOutputConfigurationForm({
        defaultValues,
        sensorStandard,
    });

    const { handleSubmit } = form;

    return (
        <ActionModal
            height={isAnalog ? 865 : 725}
            title={`${
                defaultValues === undefined ? 'Create' : 'Edit'
            } Sensor Configuration`}
            actionButtonText={defaultValues === undefined ? 'Create' : 'Edit'}
            actionable={!isSaving}
            onAction={handleSubmit(onSave)}
            onClose={onClose}
        >
            <FormLayout>
                <CreateConfigurationFormFields
                    sensorModelId={sensorModelId}
                    sensorStandard={sensorStandard}
                    slot={slot}
                    form={form}
                />
            </FormLayout>
        </ActionModal>
    );
}
